import { HttpService } from 'http/httpService'
import { Membership, MembershipDetailed } from './schema'
import { DeepPartial } from 'utility-types'
import { Comment } from 'uiComponents/comment/comment'

export interface MembershipListParams {
    accountSlug: string
    search?: string
    status?: string
    pageSize?: number
    url?: string | null
}

export interface MembershipDetailsParams {
    accountSlug: string
    uuid: string
}

export interface MembershipPaymentRefundParams {
    accountSlug: string
    uuid: string
    paymentUuid: string
}

export interface MembershipListResult {
    results: Membership[]
    previous: string | null
    next: string | null
}

export interface MembershipMutationResult {
    message: string
}

export class MembershipsService {
    constructor(private httpService: HttpService, private endpoint: string) {}

    private async handleRequest<T>(url: string, method: string, body?: string): Promise<T> {
        const response = await this.httpService.fetch(url, {
            method,
            body,
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
        })

        if (!response.ok) {
            throw new Error(`Backoffice has returned status code: ${response.status}`)
        }

        const payload = await response.json()
        if (payload.errors) {
            throw new Error(`Backoffice has returned an error: ${payload.errors[0].message}`)
        }

        return payload
    }

    async listMemberships({
        accountSlug,
        status,
        pageSize,
        search,
        url,
    }: MembershipListParams): Promise<MembershipListResult> {
        let requestUrl = url
        if (!requestUrl) {
            const queryParams = new URLSearchParams({})
            if (pageSize) queryParams.set('page_size', pageSize.toString())
            if (search) queryParams.set('search', search)
            if (status) {
                const statuses = status.split(',')
                statuses.forEach((s) => queryParams.append('status', s))
            }

            requestUrl = `${this.endpoint}api/v1/accounts/${accountSlug}/memberships/?${queryParams.toString()}`
        }

        return this.handleRequest<MembershipListResult>(requestUrl, 'GET')
    }

    async getMembershipDetails({ accountSlug, uuid }: MembershipDetailsParams): Promise<MembershipDetailed> {
        return this.handleRequest<MembershipDetailed>(
            `${this.endpoint}api/v1/accounts/${accountSlug}/memberships/${uuid}/`,
            'GET',
        )
    }

    async updateMembership({
        accountSlug,
        uuid,
        overrides,
    }: {
        accountSlug: string
        uuid: string
        overrides: DeepPartial<Membership> | { comment: Comment }
    }): Promise<MembershipDetailed> {
        return this.handleRequest<MembershipDetailed>(
            `${this.endpoint}api/v1/accounts/${accountSlug}/memberships/${uuid}/`,
            'PATCH',
            JSON.stringify(overrides),
        )
    }

    async cancelMembership({ accountSlug, uuid }: MembershipDetailsParams): Promise<MembershipMutationResult> {
        return this.handleRequest<MembershipMutationResult>(
            `${this.endpoint}api/v1/accounts/${accountSlug}/memberships/${uuid}/`,
            'POST',
            JSON.stringify({ operation: 'cancel' }),
        )
    }

    async disableAutoRenewal({ accountSlug, uuid }: MembershipDetailsParams): Promise<MembershipMutationResult> {
        return this.handleRequest<MembershipMutationResult>(
            `${this.endpoint}api/v1/accounts/${accountSlug}/memberships/${uuid}/`,
            'POST',
            JSON.stringify({ operation: 'disable_auto_renewal' }),
        )
    }

    async refundMembershipPayment({
        accountSlug,
        uuid,
        paymentUuid,
    }: MembershipPaymentRefundParams): Promise<MembershipMutationResult> {
        return this.handleRequest<MembershipMutationResult>(
            `${this.endpoint}api/v1/accounts/${accountSlug}/memberships/${uuid}/payments/${paymentUuid}/refund/`,
            'POST',
        )
    }
}
